import { z } from 'zod';
import { DeviceProtocol, DeviceStatus, DriverStatus, FuelType, PlaceType, TrackingSource, VehicleStatus, VehicleType } from '../enums.js';

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');

// ───────────────────────────── vehicles ─────────────────────────────

export const CreateVehicleSchema = z.object({
  plateAr: z.string().min(2).max(20),
  plateLatin: z.string().max(20).optional(),
  name: z.string().max(80).optional(),
  type: z.enum(VehicleType).default('TRUCK'),
  make: z.string().max(60).optional(),
  model: z.string().max(60).optional(),
  year: z.number().int().min(1980).max(2100).optional(),
  vin: z.string().max(30).optional(),
  color: z.string().max(30).optional(),
  fuelType: z.enum(FuelType).optional(),
  tankCapacityL: z.number().min(0).max(5000).optional(),
  trackingSource: z.enum(TrackingSource).default('PHONE'),
  odometerKm: z.number().min(0).max(10_000_000).default(0),
  speedLimitKph: z.number().int().min(20).max(200).nullable().optional(),
  groupId: z.string().uuid().nullable().optional(),
  photoFileId: z.string().uuid().nullable().optional(),
  notes: z.string().max(1000).optional(),
});
export type CreateVehicle = z.infer<typeof CreateVehicleSchema>;

export const UpdateVehicleSchema = CreateVehicleSchema.omit({ odometerKm: true }).partial().extend({
  status: z.enum(VehicleStatus).optional(),
});
export type UpdateVehicle = z.infer<typeof UpdateVehicleSchema>;

export const OdometerCorrectionSchema = z.object({
  odometerKm: z.number().min(0).max(10_000_000),
  note: z.string().max(200).optional(),
});
export type OdometerCorrection = z.infer<typeof OdometerCorrectionSchema>;

export const VehicleGroupInputSchema = z.object({
  name: z.string().min(1).max(60),
  color: hex.nullable().optional(),
  parentId: z.string().uuid().nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
});
export type VehicleGroupInput = z.infer<typeof VehicleGroupInputSchema>;

export const AssignDriverSchema = z.object({
  driverId: z.string().uuid(),
  startedAt: z.string().datetime({ offset: true }).optional(),
});
export type AssignDriver = z.infer<typeof AssignDriverSchema>;

// ───────────────────────────── drivers ─────────────────────────────

export const CreateDriverSchema = z.object({
  fullName: z.string().min(2).max(120),
  /** Any Egyptian mobile format; normalized to E.164. Also creates the login user. */
  phone: z.string().min(8).max(20),
  nationalId: z.string().max(20).optional(),
  licenseNumber: z.string().max(40).optional(),
  licenseClass: z.string().max(20).optional(),
  licenseExpiresAt: isoDate.nullable().optional(),
  hiredAt: isoDate.nullable().optional(),
  photoFileId: z.string().uuid().nullable().optional(),
  notes: z.string().max(1000).optional(),
});
export type CreateDriver = z.infer<typeof CreateDriverSchema>;

export const UpdateDriverSchema = CreateDriverSchema.partial().extend({ status: z.enum(DriverStatus).optional() });
export type UpdateDriver = z.infer<typeof UpdateDriverSchema>;

// ───────────────────────────── devices ─────────────────────────────

export const CreateDeviceSchema = z.object({
  imei: z.string().regex(/^\d{15}$/, '15 digits'),
  protocol: z.enum(DeviceProtocol).default('TELTONIKA_CODEC8'),
  model: z.string().max(40).optional(),
  simIccid: z.string().max(30).optional(),
  simPhone: z.string().max(20).optional(),
  simProvider: z.string().max(30).optional(),
  firmware: z.string().max(40).optional(),
  vehicleId: z.string().uuid().nullable().optional(),
  installedAt: isoDate.nullable().optional(),
  notes: z.string().max(500).optional(),
});
export type CreateDevice = z.infer<typeof CreateDeviceSchema>;

export const UpdateDeviceSchema = CreateDeviceSchema.omit({ imei: true, vehicleId: true }).partial().extend({ status: z.enum(DeviceStatus).optional() });
export type UpdateDevice = z.infer<typeof UpdateDeviceSchema>;

export const LinkDeviceSchema = z.object({ vehicleId: z.string().uuid() });

// ───────────────────────────── customers ─────────────────────────────

export const CustomerInputSchema = z.object({
  name: z.string().min(2).max(120),
  contactName: z.string().max(120).optional(),
  phone: z.string().max(20).optional(),
  email: z.string().email().max(120).optional().or(z.literal('')),
  taxId: z.string().max(30).optional(),
  address: z.string().max(300).optional(),
  notes: z.string().max(1000).optional(),
  isActive: z.boolean().default(true),
});
export type CustomerInput = z.infer<typeof CustomerInputSchema>;

// ───────────────────────────── places ─────────────────────────────

const lat = z.number().min(-90).max(90);
const lon = z.number().min(-180).max(180);
/** GeoJSON polygon ring(s): [[ [lon,lat], ... ]] — first ring is the shell. */
const polygonCoords = z.array(z.array(z.tuple([lon, lat])).min(4)).min(1);

export const PlaceInputSchema = z
  .object({
    name: z.string().min(1).max(120),
    type: z.enum(PlaceType).default('OTHER'),
    shape: z.enum(['CIRCLE', 'POLYGON']),
    centerLat: lat,
    centerLon: lon,
    radiusM: z.number().int().min(20).max(50_000).optional(),
    polygon: polygonCoords.optional(),
    address: z.string().max(300).optional(),
    customerId: z.string().uuid().nullable().optional(),
    color: hex.nullable().optional(),
    isActive: z.boolean().default(true),
  })
  .refine((p) => (p.shape === 'CIRCLE' ? p.radiusM !== undefined : p.polygon !== undefined), { message: 'CIRCLE needs radiusM; POLYGON needs polygon' });
export type PlaceInput = z.infer<typeof PlaceInputSchema>;

export const NearbyQuerySchema = z.object({
  lat: z.coerce.number().min(-90).max(90),
  lon: z.coerce.number().min(-180).max(180),
  radiusM: z.coerce.number().int().min(10).max(100_000).default(1000),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});
export type NearbyQuery = z.infer<typeof NearbyQuerySchema>;

// ───────────────────────────── search ─────────────────────────────

export interface SearchHit {
  kind: 'vehicle' | 'driver' | 'trip' | 'customer' | 'place';
  id: string;
  title: string;
  subtitle: string | null;
  href: string;
}

/** Forward-geocoding result (GET /geo/search). */
export interface GeoHit {
  label: string;
  lat: number;
  lon: number;
}
